import React from 'react';
import { TableRowProps, TableColumn } from '../../../types/DesignSystem/Table';
import TableCell from './TableCell';
import { stickyCellGround, stickyEdgeShadow } from './stickyColumn';
import { ChevronRight } from '../Iconography/Icons';
import Spinner from '../Spinner/Spinner';
import Checkbox from '../Checkbox/Checkbox';
import { FOCUS_RING, FOCUS_RING_INSET } from '../focusRing';
import { retainingDisabled, useFocusHolder } from '../focusRetention';

/**
 * Get cell value from row using accessor
 */
const getCellValue = <T,>(row: T, accessor: TableColumn<T>['accessor']): any => {
  if (typeof accessor === 'function') {
    return accessor(row);
  }
  return row[accessor as keyof T];
};

const TableRow = <T,>({
  row,
  rowIndex,
  columns,
  hoverable = false,
  striped = false,
  sunken,
  scrolled = { under: false, left: false, right: false },
  density = 'default',
  onClick,
  'data-testid': dataTestId,
  expandable = false,
  expanded = false,
  canExpand = true,
  expandLoading = false,
  onToggleExpand,
  expandLabel,
  expandedContent,
  selectable = false,
  selected = false,
  canSelect = true,
  onToggleSelect,
  selectionLabel = 'Select row',
  rowTabIndex,
  onRowFocus,
  actionHintId,
}: TableRowProps<T>) => {
  /* § 401 — whether the expand chevron holds focus (see the chevron below). */
  const { holder, bind } = useFocusHolder<'expand'>();
  /*
    `last:tw-border-b-0`: every row drew a bottom rule, including the last. Against the
    wrapper frame's own bottom border that is two 1px greys 1px apart — a double line —
    and with a footer it doubles against `TableFooter`'s `border-t` instead. Either way
    the closing rule is owned by whatever comes after the body, not by the final row.
    See `okf/design-system/e2e.md` § 38.
  */
  const rowBase = 'tw-border-b last:tw-border-b-0 tw-border-edge motion-safe:tw-transition-colors motion-safe:tw-duration-instant motion-safe:tw-ease-swift';
  /* `tw-group` is what lets a PINNED cell follow the row hover. A pinned cell has to
     paint an opaque ground (or it becomes a window onto the columns sliding under it),
     and an opaque cell hides the `<tr>`'s own `hover:` tint. `group-hover:` on the
     cell is the only form that fires from anywhere on the row rather than from that
     one cell. Added only when the table is hoverable, so a static table gains
     nothing. */
  /* § 240 — a caller-resolved ground (`isRowSunken`) wins over index parity: a zebra
     that encodes grouping (parent + expanded version rows share one ground) cannot be
     derived from the flat row index. One resolution, used for the row AND the pinned
     cells below, so a sticky cell never paints a different ground than its row. */
  const rowSunken = sunken !== undefined ? sunken : (striped && rowIndex % 2 === 1);
  /* § 299 — SELECTED outranks the zebra: the § 51 selection grammar, accent-tint under
     full ink. It also takes no hover ground below — selected is brand, hover is neutral,
     and two grounds on one row would leave the winner to stylesheet order. */
  const stripedClass = selected ? 'tw-bg-accent-tint' : rowSunken ? 'tw-bg-surface-sunken' : 'tw-bg-surface';
  /* § 256 — the hover ground is computed from the row's OWN resting ground rather
     than named absolutely. A striped or grouped row already RESTS on
     `surface-sunken`, so the old fixed `hover:tw-bg-surface-sunken` resolved to the
     colour the row was already painted: in a table that was both striped and
     hoverable, every other row was inert under the pointer. Each row now moves one
     rung instead — a sunken row up to `surface`, a plain row to the row-hover
     ground — which is also what stops the DARK hover landing on the page ground. */
  const hoverClass = hoverable
    ? selected
      ? 'tw-group tw-cursor-pointer'
      : `tw-group tw-cursor-pointer active:tw-bg-tone-neutral-tint ${rowSunken ? 'hover:tw-bg-surface' : 'hover:tw-bg-surface-hover-strong'}`
    : '';

  const handleClick = () => {
    if (onClick) {
      onClick(row, rowIndex);
    }
  };

  /*
    § 302 — a clickable row is REACHABLE. `onRowClick` used to make the <tr> a
    mouse-only target: `cursor-pointer` and nothing else, so a keyboard user could
    see the row was a link and had no way to follow it (WCAG 2.1.1). With a handler
    the row joins the tab order and Enter / Space fire it — but only when the key
    lands on the ROW ITSELF (`target === currentTarget`): a button, a box or the
    expand chevron inside keeps its own Enter without the row firing too. Space is
    prevented so it does not scroll. The ring is the INSET one, because the table
    wrapper clips (§ 8). `hoverable` alone is a hover and stays out of the tab order.
  */
  const clickable = typeof onClick === 'function';
  /*
    § 349 — and the rows are ONE tab stop. Every clickable row used to be its own, so a
    100-row table cost 100 Tab presses to cross. `Table` hands out the `tabIndex` (0 on
    the active row, -1 on the rest); from the row itself ArrowUp/ArrowDown move to the
    neighbouring clickable row and Home/End to the first and last, without wrapping —
    the grid convention. Only the ROW's own keys: arrows inside a control in the row
    stay that control's. `data-row-nav` marks the rows that take part; the sibling rows
    are found in this row's own `<tbody>`, so a nested table's rows never join.
  */
  const moveToRow = (from: HTMLTableRowElement, key: string): boolean => {
    const body = from.closest('tbody');
    if (!body) return false;
    const rows = [...body.querySelectorAll<HTMLTableRowElement>('tr[data-row-nav]')]
      .filter((el) => el.closest('tbody') === body);
    const at = rows.indexOf(from);
    const target = key === 'Home' ? rows[0]
      : key === 'End' ? rows[rows.length - 1]
        : key === 'ArrowDown' ? rows[at + 1]
          : rows[at - 1];
    if (!target) return false;
    target.focus();
    return true;
  };
  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      moveToRow(event.currentTarget, event.key);
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handleClick();
  };

  /*
    `aria-controls` has to point at a real id. § 349 — it came from the row's TESTID, so
    a table rendered without one had no link between the chevron and what it opened: a
    test hook decided an accessibility relationship. With a testid the id is unchanged
    (specs key off `…-expanded`); without one it comes from `useId`.
  */
  const uid = React.useId();
  const expandedRowId = dataTestId ? `${dataTestId}-expanded` : `${uid}-expanded`;

  return (
    <>
    <tr
      className={`${rowBase} ${stripedClass} ${hoverClass}${clickable ? ` ${FOCUS_RING_INSET}` : ''}`}
      onClick={hoverable || onClick ? handleClick : undefined}
      onKeyDown={clickable ? handleRowKeyDown : undefined}
      onFocus={clickable ? (event) => { if (event.target === event.currentTarget) onRowFocus?.(); } : undefined}
      tabIndex={clickable ? (rowTabIndex ?? 0) : undefined}
      /* § 380 — the row says what Enter does: `Table`'s one hidden `rowActionHint`. */
      aria-describedby={clickable ? actionHintId : undefined}
      data-row-nav={clickable ? 'true' : undefined}
      data-selected={selected ? 'true' : undefined}
      data-testid={dataTestId}
    >
      {selectable && (
        /* § 299 — the selection cell leads. `stopPropagation` on the CELL: the row itself
           may be clickable (`onRowClick`), and ticking a box is not opening the row. A
           row that cannot be selected gets an EMPTY cell, not a dead box (§ 69). */
        <TableCell
          density={density}
          className="tw-w-12"
          onClick={(event) => event.stopPropagation()}
          data-testid={dataTestId ? `${dataTestId}-select-cell` : undefined}
        >
          {canSelect && (
            <Checkbox
              label=""
              aria-label={selectionLabel}
              checked={selected}
              onChange={() => onToggleSelect?.()}
              data-testid={dataTestId ? `${dataTestId}-select` : undefined}
            />
          )}
        </TableCell>
      )}
      {columns.map((column) => {
        const value = getCellValue(row, column.accessor);
        const content = column.render
          ? column.render(value, row, rowIndex)
          : value;

        /* The row resolves the pinned cell's ground: it is the only place that knows
           whether THIS row is the striped one, and whether the table is hoverable. */
        const groundClass = stickyCellGround(column.sticky, {
          striped: rowSunken,
          hoverable,
          selected,
        });
        /* § 222 — while columns slide beneath the pin, the seam casts `sm` sideways
           and reads as a layer. Empty at rest; `Table` owns the measurement. */
        const edgeShadow = stickyEdgeShadow(column.sticky, scrolled);

        return (
          <TableCell
            key={column.id}
            align={column.align}
            cellType={column.cellType}
            density={density}
            sticky={column.sticky}
            className={`${groundClass} ${edgeShadow} ${column.cellClassName || ''}`.trim()}
          >
            {content}
          </TableCell>
        );
      })}

      {expandable && (
        <TableCell align="right" cellType="icon" density={density} data-testid={dataTestId ? `${dataTestId}-expand-cell` : undefined}>
          {/* A row with nothing to open gets an EMPTY cell, not a dead chevron — the
              § 69 rule against focusable no-ops. The cell still renders so the grid
              stays rectangular. */}
          {canExpand && (
            <button
              type="button"
              aria-expanded={expanded}
              aria-controls={expandedRowId}
              aria-label={expandLabel}
              /*
                `aria-busy` is how the wait is announced, and `disabled` is what stops a
                second click issuing a second request. The spinner below is
                `aria-hidden`, so its own `role="status"` does not open a live region
                inside a control — the button already carries the state.
              */
              aria-busy={expandLoading || undefined}
              /* § 401 — pressed from the keyboard, the chevron disables itself while its row
                 loads, and a natively disabled button that holds focus throws focus to <body>
                 (Party Management's Channels table). While focused it is `aria-disabled`
                 instead, and the click below refuses; unfocused it is natively disabled as
                 before. */
              {...retainingDisabled(expandLoading, holder === 'expand')}
              {...bind('expand')}
              data-testid={dataTestId ? `${dataTestId}-expand` : undefined}
              /* § 295 — `surface-hover-strong`, the § 256 row/control hover ground.
                 This hovered to `surface-sunken`, which in dark is the PAGE ground:
                 1.10:1 darker than its own row, the "hole" § 256 closed for rows and
                 menu items and missed on this one control. Byte-identical in light. */
              className={`tw-rounded-ctl tw-p-1 tw-text-ink-secondary motion-safe:tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${FOCUS_RING} ${
                expandLoading ? 'tw-cursor-wait forced-colors:tw-text-[color:GrayText]' : 'tw-cursor-pointer hover:tw-bg-surface-hover-strong active:tw-bg-tone-neutral-tint'
              }`}
              onClick={(event) => {
                /* The row itself may be clickable (`onRowClick`); expanding is not
                   navigating, so the chevron must not trigger both. */
                event.stopPropagation();
                /* § 401 — the second press `disabled` used to stop, now refused here. */
                if (expandLoading) return;
                onToggleExpand?.();
              }}
            >
              {expandLoading ? (
                <Spinner
                  size="sm"
                  aria-hidden="true"
                  data-testid={dataTestId ? `${dataTestId}-expand-loading` : undefined}
                />
              ) : (
                <ChevronRight
                  size={16}
                  aria-hidden="true"
                  className={`motion-safe:tw-transition-transform motion-safe:tw-duration-base motion-safe:tw-ease-swift ${expanded ? 'tw-rotate-90' : ''}`}
                />
              )}
            </button>
          )}
        </TableCell>
      )}
    </tr>

    {expandable && expanded && canExpand && (
      <tr
        id={expandedRowId}
        data-testid={dataTestId ? `${dataTestId}-expanded` : undefined}
        className="tw-border-b tw-border-edge"
      >
        {/* `+1` for the expand column, `+1` more for the selection column (§ 299). `p-0`
            so the caller's content owns its own padding — a nested Table brings its own
            frame. */}
        <td colSpan={columns.length + 1 + (selectable ? 1 : 0)} className="tw-p-0 tw-bg-surface-sunken">
          {expandedContent}
        </td>
      </tr>
    )}
    </>
  );
};

export default TableRow;
